import React, { useState, useRef, useEffect } from 'react';
import { PipelineNodeType } from '../../types/pipeline';
import { usePipelineBuilder } from '../../context/PipelineBuilderContext';
import { PIPELINE_NODE_TEMPLATES } from './PipelinePalette';
import { v4 as uuidv4 } from 'uuid';
import '../../styles/pipelineBuilder.css';

interface DraggedTemplate {
  nodeType: PipelineNodeType;
  startPos: { x: number; y: number };
}

export const PipelineCanvas: React.FC = () => {
  const {
    nodes,
    connections,
    selectedNodeId,
    selectNode,
    addNode,
    removeNode,
    updateNode,
    addConnection,
    removeConnection,
  } = usePipelineBuilder();

  const canvasRef = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);
  const [draggedTemplate, setDraggedTemplate] = useState<DraggedTemplate | null>(null);
  const [connectionMode, setConnectionMode] = useState(false);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!canvasRef.current) return;
    e.preventDefault();

    const zoomSpeed = 0.1;
    const newZoom = Math.max(0.3, Math.min(3, zoom + (e.deltaY > 0 ? -zoomSpeed : zoomSpeed)));
    setZoom(newZoom);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 && !connectionMode) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('nodeType') as PipelineNodeType;
    const template = PIPELINE_NODE_TEMPLATES[nodeType];

    if (!template || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    const newNode = {
      id: uuidv4(),
      type: nodeType,
      name: template.name,
      config: template.defaultConfig,
      position: { x, y },
      inputPorts: template.inputPorts,
      outputPorts: template.outputPorts,
      connections: { from: [], to: [] },
    };

    addNode(newNode);
    selectNode(newNode.id);
  };

  const handleNodeMouseDown = (
    e: React.MouseEvent,
    nodeId: string
  ) => {
    e.stopPropagation();
    if (connectionMode) {
      if (connectionStart && connectionStart !== nodeId) {
        const connection = {
          id: uuidv4(),
          fromNodeId: connectionStart,
          fromPortId: 'output',
          toNodeId: nodeId,
          toPortId: 'input',
        };
        addConnection(connection);
        setConnectionMode(false);
        setConnectionStart(null);
      } else {
        setConnectionStart(nodeId);
      }
    } else {
      selectNode(selectedNodeId === nodeId ? null : nodeId);
    }
  };

  const handleNodeContextMenu = (e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const menu = document.createElement('div');
    menu.className = 'context-menu';
    menu.style.position = 'fixed';
    menu.style.left = e.clientX + 'px';
    menu.style.top = e.clientY + 'px';
    menu.style.zIndex = '1000';
    menu.innerHTML = `
      <button data-action="delete" style="display: block; width: 100%; padding: 8px; text-align: left; border: none; background: none; cursor: pointer;">🗑️ Delete</button>
      <button data-action="connect" style="display: block; width: 100%; padding: 8px; text-align: left; border: none; background: none; cursor: pointer;">🔗 Connect</button>
      <button data-action="duplicate" style="display: block; width: 100%; padding: 8px; text-align: left; border: none; background: none; cursor: pointer;">📋 Duplicate</button>
    `;

    menu.addEventListener('click', (ev) => {
      const button = (ev.target as HTMLElement).closest('button');
      if (!button) return;
      const action = button.getAttribute('data-action');
      if (action === 'delete') removeNode(nodeId);
      if (action === 'connect') {
        setConnectionMode(true);
        setConnectionStart(nodeId);
      }
      if (action === 'duplicate') {
        const nodeToDuplicate = nodes.find(n => n.id === nodeId);
        if (nodeToDuplicate) {
          const newNode = {
            ...nodeToDuplicate,
            id: uuidv4(),
            name: `${nodeToDuplicate.name} (copy)`,
            position: {
              x: nodeToDuplicate.position.x + 50,
              y: nodeToDuplicate.position.y + 50,
            },
            connections: { from: [], to: [] },
          };
          addNode(newNode);
        }
      }
      document.body.removeChild(menu);
    });

    document.body.appendChild(menu);
    setTimeout(() => {
      if (document.body.contains(menu)) document.body.removeChild(menu);
    }, 5000);
  };

  const getNodePosition = (node: any) => {
    return {
      left: `${node.position.x}px`,
      top: `${node.position.y}px`,
    };
  };

  const isConnectionDrawing = (fromId: string, toId: string) => {
    return connections.some(c => c.fromNodeId === fromId && c.toNodeId === toId);
  };

  return (
    <div className="pipeline-canvas">
      <div className="canvas-toolbar">
        <button
          className={`mode-btn ${connectionMode ? 'active' : ''}`}
          onClick={() => {
            setConnectionMode(!connectionMode);
            setConnectionStart(null);
          }}
          title="Connect nodes mode"
        >
          🔗
        </button>
        <div className="toolbar-spacer" />
        <span className="node-count">{nodes.length} nodes</span>
        <span className="zoom-indicator">🔍 {(zoom * 100).toFixed(0)}%</span>
      </div>

      <div
        ref={canvasRef}
        className="canvas-area"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onWheel={handleWheel}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        style={{ cursor: isPanning ? 'grabbing' : connectionMode ? 'crosshair' : 'default' }}
      >
        <div
          ref={contentsRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: !isPanning && zoom === 1 ? 'none' : 'transform 0.1s ease-out',
          }}
        >
          {nodes.length === 0 ? (
            <div className="empty-canvas">
              <div className="empty-icon">⚙️</div>
              <p>Drag nodes here to build your pipeline</p>
            </div>
          ) : (
            <>
              {/* Connection lines */}
              <svg className="connections-svg">
                {connections.map((connection) => {
                  const fromNode = nodes.find(n => n.id === connection.fromNodeId);
                  const toNode = nodes.find(n => n.id === connection.toNodeId);
                  if (!fromNode || !toNode) return null;

                  const x1 = fromNode.position.x + 150;
                  const y1 = fromNode.position.y + 50;
                  const x2 = toNode.position.x;
                  const y2 = toNode.position.y + 50;

                  return (
                    <g key={connection.id}>
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        className="connection-line"
                        strokeWidth="2"
                        stroke="#8B5CF6"
                        markerEnd="url(#arrow)"
                      />
                      {connection.label && (
                        <text
                          x={(x1 + x2) / 2}
                          y={(y1 + y2) / 2 - 5}
                          className="connection-label"
                          textAnchor="middle"
                          fontSize="12"
                        >
                          {connection.label}
                        </text>
                      )}
                    </g>
                  );
                })}
                <defs>
                  <marker
                    id="arrow"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" fill="#8B5CF6" />
                  </marker>
                </defs>
              </svg>

              {/* Node elements */}
              {nodes.map((node) => {
                const template = PIPELINE_NODE_TEMPLATES[node.type];
                return (
                  <div
                    key={node.id}
                    className={`pipeline-node ${selectedNodeId === node.id ? 'selected' : ''} ${
                      connectionMode && node.id === connectionStart ? 'connection-start' : ''
                    }`}
                    style={getNodePosition(node)}
                    onClick={(e) => handleNodeMouseDown(e, node.id)}
                    onContextMenu={(e) => handleNodeContextMenu(e, node.id)}
                  >
                    <div className="node-header">
                      <span className="node-icon">{template.icon}</span>
                      <span className="node-name">{node.name}</span>
                    </div>

                    {/* Input ports */}
                    <div className="node-ports input-ports">
                      {node.inputPorts.map((port) => (
                        <div
                          key={port.id}
                          className="port input-port"
                          title={port.label}
                        >
                          <span className="port-dot" />
                          <span className="port-label">{port.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Output ports */}
                    <div className="node-ports output-ports">
                      {node.outputPorts.map((port) => (
                        <div
                          key={port.id}
                          className="port output-port"
                          title={port.label}
                        >
                          <span className="port-label">{port.label}</span>
                          <span className="port-dot" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineCanvas;
